import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {AttributeService} from '@emm/core/services/attribute.service';
import {ChangeSetService} from '@emm/core/services/change-set.service';
import {ConsumerService} from '@emm/core/services/consumer.service';
import {LocationSetTypeService} from '@emm/core/services/location-set-type.service';
import {LocationSetService} from '@emm/core/services/location-set.service';
import {MenuService} from '@emm/core/services/menu.service';
import {TagService} from '@emm/core/services/tag.service';
import {VenueService} from '@emm/core/services/venue.service';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import { Attribute } from '@emm/core/models/attribute.model';
import {Consumer} from '@emm/core/models/consumer.model';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {Submenu} from '@emm/core/models/submenu.model';
import {ItemSummary} from '@emm/core/models/item-summary.model';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {Venue} from '@emm/core/models/venue.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {ChangeSet} from '@emm/core/models/change-set.model';
import {MenuChange} from '@emm/core/models/menu-change.model';
import {ConsumerType} from '@emm/core/models/consumer-type.model';
import {MenuPublishingService} from '@emm/core/services/menu-publishing.service';
import {PublishedMenu} from '@emm/core/models/published-menu.model';


@Injectable({
    providedIn: 'root'
})
export class MenuApiService {

    constructor(
        private attributeService: AttributeService,
        private changeSetService: ChangeSetService,
        private consumerService: ConsumerService,
        private locationSetTypeService: LocationSetTypeService,
        private locationSetService: LocationSetService,
        private menuService: MenuService,
        private tagService: TagService,
        private venueService: VenueService,
        private menuPublishingService: MenuPublishingService,
    ) {}


    getMenuFilteredByItemTag(featureScope: string, tag: string, consumer?: string, effectiveDate?: string): Observable<any> {
        if (featureScope !== 'Global') {
            return this.menuService.getMenuFilteredByItemTag(tag, consumer ? consumer : null,
                effectiveDate ? effectiveDate : null);
        } else {
            return this.menuService.getMenuFilteredByItemTag(tag, null,
                effectiveDate ? effectiveDate : null);
        }
    }

    downloadMenu(endpoint: string): Observable<any> {
        return this.menuService.downloadMenu(endpoint);
    }

    downloadItemsReport(endpoint: string): Observable<any> {
        return this.menuService.downloadItemsReport(endpoint);
    }

    getLastPublishedDateForMenu(endpoint: string) {
        return this.menuService.getLastPublishedDateForMenu(endpoint);
    }

    getAllLocationSetTypes(): Observable<LocationSetType[]> {
        return this.locationSetTypeService.getAllLocationSetTypes();
    }


    getAllLocationSetsByTag(tag: string): Observable<LocationSet[]> {
        return this.locationSetService.getAllLocationSetsByTag(tag);
    }


    getAllAttributes(): Observable<Attribute[]> {
        return this.attributeService.getAllAttributes();
    }


    getAllConsumers(): Observable<Consumer[]> {
        return this.consumerService.getAllConsumers();
    }


    getSubmenusByFeatureScope(
        featureScope: string,
        menuKey: MenuKeyContext
    ): Observable<Submenu[]> {
        return this.menuService.getSubmenusV4(featureScope, menuKey);
    }


    getMenuItemChildrenByFeatureScope(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string = ''
    ): Observable<ItemSummary[]> {
        switch (featureScope) {
            case FeatureScope.Viewer:
                return menuKey.consumer && menuKey.location ?
                    this.menuService.getMenuItemsV4(effectiveDate, menuKey, path) : null;
            case FeatureScope.Global:
                return menuKey.location ?
                    this.menuService.getSubmenuItemsV4(featureScope, effectiveDate, menuKey, path) : null;
            case FeatureScope.Client:
                return menuKey.consumer && menuKey.location ?
                    this.menuService.getSubmenuItemsV4(featureScope, effectiveDate, menuKey, path) : null;
        }
    }


    getMenuItemDetailByFeatureScope(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
        language: string,
        venue?: Venue,
    ): Observable<ItemDetail> {
        switch (featureScope) {
            case FeatureScope.Viewer:
                if (venue && venue.name !== 'Base Location') {
                    return this.venueService.getMenuItemForVenue(menuKey.consumer, menuKey.location, venue.venueNumber, path);
                } else {
                    return this.menuService.getMenuItemV4(effectiveDate, menuKey, path, language);
                }
            case FeatureScope.Global:
            case FeatureScope.Client:
                return this.menuService.getSubmenuItemV4(featureScope, effectiveDate, menuKey, path, language);
        }
    }


    updateMenuItem(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string | object,
        updates: any,
        cascade: boolean = false,
        submenus?: string[],
        forceOverrideInheritance?: boolean,
        forceOverrideInheritanceMap?: any,
    ): Observable<ItemDetail> {
        return forceOverrideInheritanceMap ?
            this.menuService.updateMenuItemV4New(featureScope, effectiveDate, menuKey, path, updates, cascade, submenus,
                forceOverrideInheritanceMap) :
            this.menuService.updateMenuItemV4(featureScope, effectiveDate, menuKey, path, updates, cascade, submenus,
                forceOverrideInheritance);
    }


    moveMenuItem(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        from: string,
        to: string,
        index?: string,
    ): Observable<any> {
        return this.menuService.moveMenuItemV4(featureScope, effectiveDate, menuKey, from, to, index);
    }


    addMenuItems(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        parent: ItemDetail,
        children: ItemDetail[],
        isLegacy: boolean
    ): Observable<any> {
        return this.menuService.addMenuItemsV4(featureScope, effectiveDate, menuKey, parent, children, isLegacy);
    }


    addSubmenu(
        menuKey: MenuKeyContext,
        submenu: any,
    ): Observable<any> {
        return this.menuService.addSubmenuV4(menuKey, submenu);
    }


    addMenuItemCascade(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        submenuTag: string,
        submenus: string[],
        parentTag: string,
        childTag: string
    ): Observable<any> {
        return this.menuService.addMenuItemCascadeV4(featureScope, effectiveDate, menuKey, submenus, parentTag, childTag);
    }


    pasteMenuItemCascade(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        submenuTag: string,
        submenus: string[],
        parentTag: string,
        childTagPath: string,
        position: string
    ): Observable<any> {
        return this.menuService.pasteMenuItemCascadeV4(featureScope, effectiveDate, menuKey, submenus, parentTag,
            childTagPath, position);
    }


    deleteMenuItem(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
    ): Observable<any> {
        return this.menuService.deleteMenuItemV4(featureScope, effectiveDate, menuKey, path);
    }


    deleteMenuItemCascade(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
        submenus: string[],
    ): Observable<any> {
        return this.menuService.deleteMenuItemCascadeV4(featureScope, effectiveDate, menuKey, path, submenus);
    }


    deleteConsumer(consumerTag: string): Observable<any> {
        return this.consumerService.deleteConsumer(consumerTag);
    }


    getLocationsByParent(parentTag: string): Observable<LocationSet[]> {
        return this.locationSetService.getLocationsByParent(parentTag);
    }


    pasteMenuItem(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        fromPath: string,
        toPath: string
    ): Observable<any> {
        return this.menuService.pasteMenuItemV4(featureScope, effectiveDate, menuKey, fromPath, toPath);
    }


    getAllFutureChangeSets(): Observable<ChangeSet[]> {
        return this.changeSetService.getAllFutureChangeSetsV4();
    }


    getFutureChangeSets(
        featureScope: string,
        consumer: string,
        location: string,
        date: string
    ): Observable<ChangeSet[]> {
        return this.changeSetService.getFutureChangeSetsV4(featureScope, consumer, location, date);
    }


    getFutureChangeSetsForSubmenu(
        submenu: string,
        location: string,
        date: string
    ): Observable<ChangeSet[]> {
        return this.changeSetService.getFutureChangeSetsForSubmenuV4(submenu, location);
    }



    getChangeSetChanges(
        featureScope: string,
        changeSetId: number,
        pageIndex: number,
        pageSize: number,
        submenu: string,
        date: string,
        location: string,
    ): Observable<MenuChange[]> {
        return this.changeSetService.getChangeSetChangesV4(featureScope, submenu, date, location, pageIndex, pageSize);
    }


    getChangeCount(
        featureScope: string,
        changeSetId: number,
        submenu: string,
        date: string,
        location: string
    ): Observable<number> {
        return this.changeSetService.getChangeCountV4(featureScope, submenu, date, location);
    }


    getConsumerTypes(): Observable<ConsumerType[]> {
        return this.consumerService.getConsumerTypes();
    }


    getConsumerTags(tag: string): Observable<boolean> {
        return this.tagService.getConsumerTags(tag);
    }


    saveConsumerMenuAssignments(
        assignments: any[],
        menuKey: MenuKeyContext
    ): Observable<any> {
        return this.menuService.saveConsumerMenuAssignments(assignments, menuKey);
    }

    renameSubmenu(submenuId: string, newName: string, effectiveDate?: string): Observable<any> {
        if (effectiveDate) {
            return this.menuService.renameSubmenu(submenuId, newName, effectiveDate);
        } else {
            return this.menuService.renameSubmenu(submenuId, newName);
        }
    }

    getVenuesByLocation(location: string): Observable<Venue[]> {
        return this.venueService.getVenuesByLocation(location);
    }

    getConsumersByVenue(venueId: string | number): Observable<Consumer[]> {
        return this.venueService.getConsumersByVenue(venueId);
    }

    getVenueMenu(location: string, venueNumber: string | number, effectiveDate?: string): Observable<Venue> {
        return this.venueService.getVenueMenu(location, venueNumber, effectiveDate);
    }

    getMenuItemForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                        effectiveDate?: string): Observable<ItemDetail> {
        return this.venueService.getMenuItemForVenue(consumer, location, venueNumber, path, effectiveDate);
    }

       getMenuItemChildrenForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                                effectiveDate?: string): Observable<ItemSummary[]> {
        return this.venueService.getMenuItemChildrenForVenue(consumer, location, venueNumber, path, effectiveDate);
    }

    getAttributeOverrides(attribute: string, location: string, submenu: string, itemPath: string,
                          effectiveDate: string): Observable<any> {
        return this.attributeService.getAttributeOverrides(attribute, location, submenu, itemPath, effectiveDate);
    }

    getParentOverrides(attribute: string, location: string, submenu: string, effectiveDate: string,
                              itemPath: string, language: string) {
        return this.attributeService.getParentOverrides(attribute, location, submenu, effectiveDate, itemPath, language);
    }

    publishChainwide(consumer: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishChainwide(consumer);
    }
    publishChainwideDryRun(consumer: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishChainwideDryRun(consumer);
    }
    publishNational(countryCode: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        return this.menuPublishingService.publishNational(countryCode, consumer, includeStores);
    }
    publishNationalDryRun(countryCode: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        return this.menuPublishingService.publishNationalDryRun(countryCode, consumer, includeStores);
    }
    publishLocationSet(locationSetTag: string, consumer: string, includeStores?: boolean,): Observable<PublishedMenu> {
        return this.menuPublishingService.publishLocationSet(locationSetTag, consumer, includeStores);
    }
    publishLocationSetDryRun(locationSetTag: string, consumer: string, includeStores?: boolean): Observable<PublishedMenu> {
        return this.menuPublishingService.publishLocationSetDryRun(locationSetTag, consumer, includeStores);
    }
    publishByStore(locationTag: string, consumer: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishByStore(locationTag, consumer);
    }
    publishByStoreDryRun(locationTag: string, consumer: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishByStoreDryRun(locationTag, consumer);
    }
    publishVenue(venueId: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishVenue(venueId);
    }
    publishVenueDryRun(venueId: string): Observable<PublishedMenu> {
        return this.menuPublishingService.publishVenueDryRun(venueId);
    }
}
